import { useState } from 'react';

import { AlertBox } from '@iwoca/orion';
import { clsx } from 'clsx';

import { iwocapayTelDisplay } from '../../../../constants.json';
import downVector from '../../assets/downVector.svg';
import exclamationTriangle from '../../assets/exclamationTriangle.svg';
import stepOneSVG from '../../assets/stepOne.svg';
import stepTwoSVG from '../../assets/stepTwo.svg';
import { useGetIntegrationStatus } from '../../hooks/useGetIntegrationStatus.hook';
import { IntegrationTickAlert } from '../IntegrationTickAlert/IntegrationTickAlert';

const WOOCOMMERCE_ID = 'woocommerce';

export const WooCommerceStep1 = () => {
  const { integrationStatus } = useGetIntegrationStatus({
    integrationId: WOOCOMMERCE_ID,
  });

  const isComplete =
    integrationStatus &&
    ['GENERATED_KEYS', 'CONNECTED'].includes(integrationStatus);

  return (
    <div className="grid grid-cols-[6rem_1fr] grid-rows-[auto_1fr] pb-l pl-0 pr-r pt-l max-600:grid-rows-[auto]">
      <div className="row-span-2 row-start-1 flex flex-col justify-start max-600:row-span-1 max-600:flex">
        <img
          src={stepOneSVG}
          alt="Step One"
          className={clsx('self-center', { 'opacity-50': isComplete })}
        />
      </div>
      <div className="flex justify-between max-600:flex-col max-600:content-start">
        <div
          className={clsx(
            'm-0 flex flex-wrap justify-start pt-xs text-l font-med max-600:my-[0.5rem] max-600:flex-col max-600:pt-0',
            { 'opacity-50': isComplete },
          )}
        >
          <div className="m-0 whitespace-nowrap text-l font-med">Step 1</div>
          <div className="m-0 px-s font-med max-600:hidden">-</div>
          <div>We'll be in touch soon</div>
        </div>
        {isComplete && <IntegrationTickAlert text="Step complete" />}
      </div>
      <div className="pt-xl max-600:col-span-2 max-600:col-start-1 max-600:pt-[0.5rem]">
        <p
          className={clsx('m-0 p-0 text-s font-reg', {
            'opacity-50 max-600:hidden': isComplete,
          })}
        >
          Your account manager will run through the process, answer any
          questions and kick things off. Something urgent? You can contact us
          now on {iwocapayTelDisplay}.
        </p>
      </div>
    </div>
  );
};

export const WooCommerceStep2 = () => {
  const [showBody, setShowBody] = useState(false);
  const handleClick = () => {
    setShowBody(!showBody);
  };

  const { integrationStatus } = useGetIntegrationStatus({
    integrationId: WOOCOMMERCE_ID,
  });

  const isComplete =
    integrationStatus && ['CONNECTED'].includes(integrationStatus);

  const isActiveStep =
    integrationStatus && ['GENERATED_KEYS'].includes(integrationStatus);

  return (
    <div className="grid grid-cols-[6rem_1fr] grid-rows-[auto_1fr] pb-l pl-0 pr-r pt-l max-600:grid-rows-[auto]">
      <div className="row-span-2 row-start-1 flex flex-col justify-start max-600:row-span-1 max-600:flex">
        <img
          src={stepTwoSVG}
          alt="Step Two"
          className={clsx('self-center', { 'opacity-50': isComplete })}
        />
      </div>
      <div className="flex justify-between max-600:flex-col max-600:content-start">
        <div
          className={clsx(
            'm-0 flex flex-wrap justify-start pt-xs text-l font-med max-600:my-[0.5rem] max-600:flex-col max-600:pt-0',
            { 'opacity-50': isComplete },
          )}
        >
          <div className="m-0 whitespace-nowrap text-l font-med">Step 2</div>
          <div className="m-0 px-s font-med max-600:hidden">-</div>
          <div>Add iwocaPay to your WooCommerce store</div>
        </div>
        {isComplete && <IntegrationTickAlert text="Step complete" />}
      </div>
      <div className="pt-xl max-600:col-span-2 max-600:col-start-1 max-600:pt-[0.5rem]">
        {!isActiveStep && !isComplete && (
          <button
            className={clsx(
              'cursor-pointer border-none bg-none p-0 text-s font-bold text-primary-40',
              { 'mb-xl': showBody },
            )}
            onClick={handleClick}
          >
            {showBody && 'Close install walkthrough'}
            {!showBody && 'Go to install walkthrough'}
            <img
              src={downVector}
              alt="Down Arrow"
              className={clsx('px-s', { 'rotate-180 pt-[0.15rem]': showBody })}
            />
          </button>
        )}
        {(isActiveStep || showBody) && <WooCommerceStep2Body />}
      </div>
    </div>
  );
};

const WooCommerceStep2Body = () => {
  return (
    <div className="m-0 p-0 text-s font-reg">
      <div className="text-m font-bold">Watch the walkthrough</div>
      <iframe
        className="my-l aspect-video w-full"
        src="https://www.youtube.com/embed/X9p3HdZLQmc"
        title="YouTube video player"
        frameBorder="0"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture fullscreen"
        allowFullScreen
      ></iframe>
      <div className="text-m font-bold">Or follow these simple steps</div>
      <ul className="pl-[2rem] text-s font-reg">
        <li className="mb-l">
          Login to your Wordpress account and click <b>‘Plugins’</b> on the left
          panel then <b>‘Add New’</b>.
        </li>
        <li className="mb-l">
          Type iwocaPay in the search bar to find it then click{' '}
          <b>‘Install Now’</b>.
        </li>
        <li className="mb-l">
          Once it’s installed click <b>‘Activate’</b> to make it available to
          configure in your settings.
        </li>
        <li className="mb-l">
          Now iwocaPay should appear in the <b>Settings</b> area of your
          WooCommerce store under <b>Payments</b>.
        </li>
        <li className="mb-l">
          Click <b>‘Finish Set Up’</b> then tick the <b>‘Enable’</b> box.
        </li>
        <li className="mb-l">
          Select which payment options you’d like to show (Pay Later and Pay Now
          or just Pay Later).
        </li>
        <li className="mb-l">
          Enter your <b>API key and Seller ID</b> in the input fields and hit
          <b>&nbsp;‘Save Changes’</b>.
        </li>
      </ul>
      <div>
        And you’re done! Your store checkout will now have iwocaPay as a payment
        option.
      </div>
      <div className="h-l" />
      <AlertBox
        variant="failure"
        className="flex items-start justify-start gap-[0.7rem] !text-s !font-reg"
      >
        <img src={exclamationTriangle} alt="Warning Icon" />
        <div>
          <b>Can’t find your API key? </b>Your account manager will provide you
          with your unique API keys. If you're missing your keys, please get in
          touch and we'll sort it.
        </div>
      </AlertBox>
    </div>
  );
};
